/**
 * Helpers for commonly used parsing methods
 */

import { createInitialObject } from './utils';
let ParsingUtils;

/**
 * Parses a float/decimal
 * @param  {number|string} value      - the value to parse
 * @param  {number} [decimalsCount=4] - number of decimals
 * @return {number} the parsed float with the correct number of decimals, or 0 if it couldn't be parsed
 */
export const parseDecimal = (value, decimalsCount = 4) => {
    const parsed = parseFloat(value) || 0;
    return parseFloat(parsed.toFixed(decimalsCount));
};

/**
* Parses a value into an integer. If the value is a float, it will be rounded properly
* @param  {number|string} value - the value to parse
* @return {number} the parsed integer, or 0 if it couldn't be parsed
*/
export const parseInteger = value => ParsingUtils.parseDecimal(value, 0);

const NO_SHOW_STATUSES = ['No-Show', 'No-show'];
const STATUS_MAPPING = {
    guaranteed: ['Reserved', 'In-House', 'In-house', 'Checked-Out', 'Checked out'].concat(NO_SHOW_STATUSES),
    nonGuaranteed: ['Optional'],
    tentative: ['Requested', 'Waitlisted'],
    cancelled: ['Cancelled'],
    noShow: NO_SHOW_STATUSES,
};

/**
 * Filter all rows where the status doesn't match the requested type
 * And sum all values of the given property/key
 * @param  {Object[]} array - the array of objects to sum
 * @param  {string}   prop  - property holding value to sum
 * @param  {string}   type - type of data, one of the keys of STATUS_MAPPING
 * @return {number} the sum
 */
const getSumByType = (array, prop, type) => (
    array.filter(row => ParsingUtils.STATUS_MAPPING[type].includes(row.status))
        .reduce(
            (prev, cur) => prev + ParsingUtils.parseInteger(cur[prop]),
            0,
        )
);

export const getGuaranteedInfo = (array, prop) => ParsingUtils.getSumByType(array, prop, 'guaranteed');
export const getNonGuaranteedInfo = (array, prop) => ParsingUtils.getSumByType(array, prop, 'nonGuaranteed');
export const getTentativeInfo = (array, prop) => ParsingUtils.getSumByType(array, prop, 'tentative');
export const getCancelledInfo = (array, prop) => ParsingUtils.getSumByType(array, prop, 'cancelled');
export const getNoShowInfo = (array, prop) => ParsingUtils.getSumByType(array, prop, 'noShow');

export const getStatus = reservationStatus => {
    if (STATUS_MAPPING.guaranteed.includes(reservationStatus)) return 'guaranteed';
    if (STATUS_MAPPING.nonGuaranteed.includes(reservationStatus)) return 'nonGuaranteed';
    if (STATUS_MAPPING.tentative.includes(reservationStatus)) return 'tentative';
    if (STATUS_MAPPING.cancelled.includes(reservationStatus)) return 'cancelled';
    return null;
};

export const CUMULATED_ROOM_FIGURES_KEYS = [
    'totalRooms',
    'guaranteedRooms',
    'nonGuaranteedRooms',
    'tentativeRooms',
    'noShowRooms',
    'cancelledRooms',
];
export const CUMULATED_REVENUE_FIGURES_KEYS = [
    'totalRevenue',
    'guaranteedRevenue',
    'nonGuaranteedRevenue',
    'tentativeRevenue',
    'noShowRevenue',
    'cancelledRevenue',
];
export const CUMULATED_ROOM_REVENUE_FIGURES_KEYS = CUMULATED_ROOM_FIGURES_KEYS.concat(CUMULATED_REVENUE_FIGURES_KEYS);

export const BUDGET_FIGURES_KEYS = [
    'roomsBudget',
    'accommodationRevenueBudget',
    'ancillaryRevenueBudget',
    'fbRevenueBudget',
    'otherRevenueBudget',
    'adrBudget',
    'revParBudget',
];

/**
 * Retrieve all guar/nonGuar/tentative/cancelled figures of a certain type
 * @param  {Object[]} groupedData - data to calculate figures from
 * @param  {string} type        - "Rooms" or "Revenue"
 * @param  {string} parsingKey  - property holding value to parse
 * @return {object} an object with the guar/nonGuar/tentative/cancelled figures
 */
const getCumulatedGenericFigures = (groupedData, type, parsingKey) => {
    const statuses = [
        { label: 'guaranteed', getter: ParsingUtils.getGuaranteedInfo },
        { label: 'nonGuaranteed', getter: ParsingUtils.getNonGuaranteedInfo },
        { label: 'tentative', getter: ParsingUtils.getTentativeInfo },
        { label: 'cancelled', getter: ParsingUtils.getCancelledInfo },
    ];
    const totalKey = `total${type}`;

    if (!groupedData || groupedData.length === 0) {
        const keys = statuses.map(status => `${status.label}${type}`).concat([totalKey]);
        return createInitialObject(keys, 0);
    }

    return statuses.reduce((object, { label, getter }) => {
        const value = getter(groupedData, parsingKey);
        return {
            ...object,
            [`${label}${type}`]: value,
            [totalKey]: object[totalKey] + (label === 'cancelled' ? 0 : value),
        };
    }, { [totalKey]: 0 });
};

/**
 * Returned the cumulated values for guaranteed/unguaranteed rooms
 * @param  {Object[]} groupedData - the array with figures grouped by status
 * @param  {string} roomKey     - the property the room figure is stored in
 * @return {Object} the total/guaranteed/nonGuaranteed/tentative/cancelled rooms
 */
export const getCumulatedRoomFigures = (groupedData, roomKey) => (
    ParsingUtils.getCumulatedGenericFigures(
        groupedData,
        'Rooms',
        roomKey,
    )
);

/**
 * Returned the cumulated values for guaranteed/unguaranteed revenue
 * @param  {Object[]} groupedData - the array with figures grouped by status
 * @param  {string} roomKey     - the property the room figure is stored in
 * @return {Object} the total/guaranteed/nonGuaranteed/tentative/cancelled revenue
 */
export const getCumulatedRevenueFigures = (groupedData, revenueKey) => (
    ParsingUtils.getCumulatedGenericFigures(
        groupedData,
        'Revenue',
        revenueKey,
    )
);

/**
 * Returned the cumulated values for guaranteed/unguaranteed rooms & revenue
 * @param  {Object[]} groupedData - the array with figures grouped by status
 * @param  {string} roomKey     - the property the room figure is stored in
 * @param  {string} revenueKey  - the property the revenue figure is stored in
 * @return {Object} the total/guaranteed/nonGuaranteed/tentative/cancelled Rooms & Revenue figure
 */
export const getCumulatedRoomRevenueFigures = (groupedData, roomKey, revenueKey) => ({
    ...ParsingUtils.getCumulatedRoomFigures(groupedData, roomKey),
    ...ParsingUtils.getCumulatedRevenueFigures(groupedData, revenueKey),
});

export default ParsingUtils = {
    STATUS_MAPPING,
    parseInteger,
    parseDecimal,
    getSumByType,
    getGuaranteedInfo,
    getNonGuaranteedInfo,
    getTentativeInfo,
    getNoShowInfo,
    getCancelledInfo,
    getStatus,
    getCumulatedGenericFigures,
    getCumulatedRoomFigures,
    getCumulatedRevenueFigures,
    getCumulatedRoomRevenueFigures,
};
